import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { AlertTriangle, Battery, Droplets, Wind, FlaskConical, CheckCircle, ShieldAlert, MapPin } from 'lucide-react';

const DangerousGoodsSection = () => {
  const { t } = useLanguage();

  const dangerousItems = [
    { icon: Battery, text: t('dangerousGoods.products.0') },
    { icon: Droplets, text: t('dangerousGoods.products.1') },
    { icon: Wind, text: t('dangerousGoods.products.2') },
    { icon: FlaskConical, text: t('dangerousGoods.products.3') }
  ];

  const declareOptions = t('dangerousGoods.declareOptions');

  return (
    <section
      id="dangerous-goods"
      data-testid="dangerous-goods-section"
      className="py-24 md:py-32 bg-[#18181B] relative overflow-hidden dg-section"
    >
      {/* Warning stripes at top */}
      <div className="absolute top-0 left-0 right-0 h-2 warning-stripes" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="eyebrow eyebrow-warning mb-4">
            <AlertTriangle className="w-4 h-4" />
            {t('dangerousGoods.label')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {t('dangerousGoods.title')}
            <br />
            <span className="text-yellow-500">{t('dangerousGoods.titleAccent')}</span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-3xl mx-auto text-lg">
            {t('dangerousGoods.intro')}
          </p>
        </motion.div>

        {/* ⚠ MIAMI-ONLY DG NOTICE */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-14 relative overflow-hidden border border-yellow-500/40 bg-gradient-to-r from-yellow-500/10 via-yellow-500/5 to-transparent"
        >
          {/* Animated warning stripe accent */}
          <div className="absolute left-0 top-0 bottom-0 w-1.5 warning-stripes" />
          <div className="px-6 md:px-8 py-5 md:py-6 pl-8 md:pl-10 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center shrink-0">
                <ShieldAlert className="w-6 h-6 text-yellow-500" />
              </div>
              <div className="md:hidden">
                <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-yellow-500">
                  {t('dangerousGoods.miamiOnly.badge')}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <div className="hidden md:block">
                <span className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-yellow-500">
                  {t('dangerousGoods.miamiOnly.badge')}
                </span>
              </div>
              <h3 className="text-white text-lg md:text-xl font-bold mt-1 leading-snug">
                {t('dangerousGoods.miamiOnly.title')}
              </h3>
              <p className="text-gray-300 text-sm md:text-base mt-1 leading-relaxed">
                {t('dangerousGoods.miamiOnly.description')}
              </p>
            </div>
            <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 px-4 py-2.5 md:py-3 shrink-0">
              <MapPin className="w-4 h-4 text-yellow-500" />
              <div className="leading-tight">
                <p className="text-[10px] font-mono uppercase tracking-widest text-yellow-500/80">
                  {t('dangerousGoods.miamiOnly.locationLabel')}
                </p>
                <p className="text-white font-bold text-sm md:text-base">
                  Miami, FL · USA
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left column - Header and intro */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Warning box */}
            <div className="border-l-4 border-yellow-500 bg-yellow-500/5 p-6">
              <p className="text-yellow-500 font-medium">
                {t('dangerousGoods.warning')}
              </p>
            </div>

            {/* Image */}
            <div className="mt-8 relative overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/6169139/pexels-photo-6169139.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Logistics"
                className="w-full h-64 object-cover opacity-70"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#18181B] to-transparent" />
            </div>
          </motion.div>

          {/* Right column - Products and declaration */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Dangerous goods list */}
            <div className="mb-10">
              <h3 className="text-xl font-bold text-white mb-6 tracking-tight">
                {t('dangerousGoods.productsTitle')}
              </h3>
              <div className="space-y-4">
                {dangerousItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-4 p-4 border border-white/10 bg-white/5 hover:border-yellow-500/50 transition-colors group"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-yellow-500/10 group-hover:bg-yellow-500/20 transition-colors">
                        <Icon className="w-5 h-5 text-yellow-500" />
                      </div>
                      <p className="text-gray-300 flex-1">{item.text}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* How to declare */}
            <div className="border-t border-white/10 pt-10">
              <h3 className="text-xl font-bold text-white mb-6 tracking-tight">
                {t('dangerousGoods.howToDeclare')}
              </h3>
              <div className="space-y-4">
                {Array.isArray(declareOptions) && declareOptions.map((option, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <CheckCircle className="w-5 h-5 text-[#7AC142] mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300">{option}</p>
                  </motion.div>
                ))}
              </div>

              {/* Note */}
              <div className="mt-8 p-6 bg-[#7AC142]/10 border border-[#7AC142]/20">
                <p className="text-[#7AC142] text-sm font-medium">
                  {t('dangerousGoods.note')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DangerousGoodsSection;
